import { VisibilityOff } from "@mui/icons-material";
import { Box, CircularProgress, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useState, useEffect } from "react";
import { useRevalidator } from "react-router-dom";

import { inject } from "../../../../lib/itn-ui-kit/services/service-container";
import useService from "../../../hooks/useService";
import ApiRequestErrorHandler from "../../global/ApiRequestErrorHandler";
import FormatISODate from "../../global/FormatISODate";
import PermissionWrapper from "../../global/PermissionWrapper";
import BasicButton from "../../ui library/components/buttons/BasicButton";
import PaginationDefault from "../../ui library/components/paginations/pagination-default";

import Modals from "./components/Modals";
import getReferralColumns from "./config/referralColumns";
import { STRINGS } from "./utils/Strings";

export default function Referrals({ caseInfo }) {
  const referralsRepository = useService("repositories/referral-repository");
  // const providerClientStatusRepository = useService(
  //   "repositories/provider-client-status-repository"
  // );
  const providerRepository = useService("repositories/provider-repository");
  const fieldOptionRepository = useService("repositories/field-option-repository");
  const providerTypesRepository = useService("repositories/provider-type-repository");
  const [errorArray, setErrorArray] = useState([]);
  const [loading, setLoading] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [sources, setSources] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [selectedReferrals, setSelectedReferrals] = useState([]);
  const [limit, setLimit] = useState(STRINGS.DEFAULT_LIMIT);
  const [offset, setOffset] = useState(STRINGS.DEFAULT_OFFSET);
  const [totalCount, setTotalCount] = useState(0);
  const [editingRow, setEditingRow] = useState([]);
  const [selectedReferralFrom, setSelectedReferralFrom] = useState(null);
  const [selectedReferralTo, setSelectedReferralTo] = useState(null);
  const [selectedProviderType, setSelectedProviderType] = useState(null);
  const [selectedReferralDate, setSelectedReferralDate] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [referralTo, setReferralTo] = useState(null);
  const [mode, setMode] = useState(STRINGS.MODE.EDIT);
  const [referralEditList, setReferralEditList] = useState([]);
  const [modals, setModals] = useState({
    add: false,
    success: false,
    error: false,
    delete: false,
  });
  const [modalInfo, setModalInfo] = useState({
    title: "",
    desc: "",
  });
  const currentUserService = inject("current-user-service");
  const permissions = {
    CREATE: currentUserService?.userRoles.permissions.includes("provider.add_providerreferral"),
    READ: currentUserService?.userRoles.permissions.includes("provider.view_providerreferral"),
    UPDATE: currentUserService?.userRoles.permissions.includes("provider.change_providerreferral"),
    DELETE: currentUserService?.userRoles.permissions.includes("provider.delete_providerreferral"),
  };

  const { revalidate } = useRevalidator();

  const fetchSourceData = async () => {
    const data = await fieldOptionRepository.query({
      content_type: "providerreferral",
      field_name: "source",
    });
    setSources(data);
  };

  const fetchStatusData = async () => {
    const data = await fieldOptionRepository.query({
      content_type: "providerreferral",
      field_name: "status",
    });
    setStatuses(data);
  };

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      await Promise.all([
        fetchSourceData(),
        fetchStatusData(),
        referralsRepository
          .query({
            client: caseInfo?.client?.id,
            limit,
            offset,
            ordering: "-created_at",
          })
          .then((data) => {
            setReferrals(data);
            return referralsRepository.getTotalCount();
          })
          .then(setTotalCount),
      ]);
      setLoading(false);
    };
    fetchAll();
  }, [caseInfo, limit, offset]);

  const closeModals = () => setModals((m) => ({ ...m, add: false, success: false, delete: false }));

  const handleRequestSuccess = async () => {
    closeModals();
    setLoading(true);
    setOffset(STRINGS.DEFAULT_OFFSET);
    setLimit(STRINGS.DEFAULT_LIMIT);
    setSelectedReferrals([]);
    setReferralTo(null);
    const data = await referralsRepository.query({
      client: caseInfo?.client?.id,
      limit: STRINGS.DEFAULT_LIMIT,
      offset: STRINGS.DEFAULT_OFFSET,
      ordering: "-created_at",
    });
    setReferrals(data);
    setTotalCount(await referralsRepository.getTotalCount());
    setLoading(false);
    revalidate();
  };

  const handleRequestFailure = (error) => {
    setModals((m) => ({ ...m, error: true }));
    const errArr = ApiRequestErrorHandler(error?.response?.data || error);
    setErrorArray(errArr);
  };

  const parseData = (data) => {
    return data.map((item) => ({
      id: item?.id,
      referralFrom: item?.referralFrom,
      referralTo: item?.referralTo || null,
      client: caseInfo?.client?.id,
      referredAt: FormatISODate(item?.referredAt ?? new Date(), "yyyy-MM-dd"),
      providerType:
        item?.referralTo?.id &&
        referralTo.providerTypes &&
        validateProviderType(referralTo.providerTypes, item?.providerType)
          ? item?.providerType
          : item?.referralTo?.id
            ? null
            : item?.providerType,
      source: item?.source ? parseInt(item?.source) : null,
      ...(item?.status ? { status: parseInt(item?.status) } : {}),
    }));
  };

  const validateProviderType = (providerTypesList, providerType) =>
    providerTypesList?.some((item) => item?.id === providerType);

  const handleSuccessModal = (title, description) => {
    setModalInfo({ title, desc: description });
    setModals((m) => ({ ...m, success: true }));
  };

  const handleFailureModal = (title, description, error) => {
    setModalInfo({ title, desc: description });
    handleRequestFailure(error);
  };

  const handleOnClickAddReferral = () => {
    setModals((m) => ({ ...m, add: true }));
  };

  function getRowId(row) {
    return row.id;
  }

  const handleCheckbox = (e, id) => {
    if (id) {
      if (e.target.checked) {
        setSelectedReferrals((prev) => [...prev, id]);
      } else {
        setSelectedReferrals(selectedReferrals.filter((referralId) => referralId !== id));
      }
    }
  };

  const handleStartEditing = (referralId) => {
    const selectedRow = referrals.find((r) => r.id === referralId);

    if (!selectedRow) {
      return;
    }

    const newEditable = {
      id: referralId,
      referralFrom: selectedRow?.referralFrom?.id ?? selectedRow?.referralFrom?.pk,
      referralTo: selectedRow?.referralTo?.id ?? selectedRow?.referralTo?.pk,
      providerType: selectedRow?.providerType?.id || null,
      referredAt: selectedRow?.referredAt || null,
      source: selectedRow?.source || null,
      status: selectedRow?.status || null,
    };

    setReferralEditList((prev) => {
      const alreadyExists = prev.find((item) => item?.id === referralId);
      return alreadyExists ? prev : [...prev, newEditable];
    });

    setSelectedReferralFrom({
      id: selectedRow?.referralFrom?.id,
      key: selectedRow?.referralFrom?.id,
      label: selectedRow?.referralFrom?.name,
      providerTypes: selectedRow?.referralFrom?.providerTypes,
    });

    const referralToRow = selectedRow?.referralTo?.id
      ? selectedRow?.referralTo?.id
      : selectedRow?.referralTo?.pk;

    setSelectedReferralTo({
      id: referralToRow ?? null,
      key: referralToRow ?? null,
      label: selectedRow?.referralTo?.name ? selectedRow?.referralTo?.name : "",
      providerTypes: referralToRow ? selectedRow?.referralTo?.providerTypes : [],
    });

    if (referralToRow) {
      setReferralTo({
        id: referralToRow,
        key: referralToRow,
        label: selectedRow?.referralTo?.name,
        providerTypes: selectedRow?.referralTo?.providerTypes,
      });
    }

    setSelectedProviderType(
      selectedRow?.providerType
        ? {
            id: selectedRow?.providerType.id,
            key: selectedRow?.providerType.id,
            label: selectedRow?.providerType.name,
          }
        : null
    );
    setSelectedReferralDate(selectedRow?.referredAt);

    const source = sources.find((item) => item?.key === selectedRow?.source);
    setSelectedSource(
      selectedRow?.source
        ? {
            id: source?.key,
            key: source?.key,
            label: source?.label,
          }
        : null
    );

    const status = statuses.find((item) => item?.key === selectedRow?.status);
    setSelectedStatus(
      selectedRow?.status
        ? {
            id: status?.key,
            key: status?.key,
            label: status?.label,
          }
        : null
    );

    setEditingRow((prev) => ({
      ...prev,
      [referralId]: true,
    }));
  };

  const handleSaveEditing = async (referralId) => {
    try {
      const parsedPostData = parseData(referralEditList);

      await referralsRepository.updateBulk(parsedPostData).then((_) => {
        resetAfterEdit(referralId);
        handleSuccessModal("Success", "The referral was updated successfully.");
      });
    } catch (error) {
      handleFailureModal("Failure", "Failed to update the referral", error);
    }
  };

  const handleCancelEditing = (referralId) => {
    resetAfterEdit(referralId);
  };

  const resetAfterEdit = (referralId) => {
    setEditingRow((prev) => {
      const newState = { ...prev };
      delete newState[referralId];
      return newState;
    });

    setReferralEditList((prev) => prev.filter((item) => item?.id !== referralId));

    setMode(STRINGS.MODE.EDIT);
  };

  const handleOnEditRow = (id, param, value) => {
    if (!id || !param) {
      return;
    }
    setReferralEditList((prev) =>
      prev.map((item) => (item?.id === id ? { ...item, [param]: value?.id ?? value } : item))
    );
  };

  const columns = getReferralColumns(caseInfo?.id, {
    permissions,
    selectedReferrals,
    handleCheckbox,
    editingRow,
    selectedReferralFrom,
    providerRepository,
    handleOnEditRow,
    selectedReferralTo,
    setReferralTo,
    setSelectedProviderType,
    setMode,
    referralTo,
    selectedProviderType,
    providerTypesRepository,
    mode,
    selectedReferralDate,
    selectedStatus,
    fieldOptionRepository,
    statuses,
    selectedSource,
    sources,
    handleSaveEditing,
    handleCancelEditing,
    handleStartEditing,
  });

  return (
    <PermissionWrapper
      permission={permissions["READ"]}
      alternateChildren={
        <div className="flex w-full flex-row items-center justify-center gap-2 p-5">
          <VisibilityOff className="text-gray-500" />
          <Typography className="text-center text-lg text-gray-500">
            You do not have the permission to view referrals.
          </Typography>
        </div>
      }
    >
      <div className="flex h-full w-full flex-col items-center justify-center">
        {loading ? (
          <div className="flex h-40 w-full items-center justify-center">
            <CircularProgress color="secondary" />
          </div>
        ) : (
          <>
            <div className="flex w-full flex-row justify-end gap-4 px-2 py-4">
              {selectedReferrals.length > 0 && (
                <BasicButton
                  disabled={!permissions["DELETE"]}
                  text="Delete"
                  className={`w-32 bg-red-600 hover:bg-red-700`}
                  onClick={() => setModals((m) => ({ ...m, delete: true }))}
                />
              )}

              <BasicButton
                disabled={!permissions["CREATE"]}
                variant="outlined"
                text="Add Referral"
                className={`w-32`}
                onClick={handleOnClickAddReferral}
              />
            </div>
            <div className="w-full px-2">
              <Box className="h-[calc(100vh-200px)]">
                <DataGrid
                  key={Object.keys(editingRow).join("-")}
                  rows={referrals || []}
                  columns={columns || []}
                  getRowId={getRowId}
                  disableRowSelectionOnClick
                  disableMultipleRowSelection
                  hideFooter
                  columnVisibilityModel={{
                    providerTypes: Object.keys(editingRow).length > 0,
                  }}
                  getRowHeight={() => "auto"}
                  sx={{
                    "& .MuiDataGrid-columnSeparator": {
                      display: "none",
                    },
                    "& .MuiDataGrid-columnHeader .MuiDataGrid-columnHeaderTitle": {
                      whiteSpace: "normal",
                      lineHeight: "1.2",
                      textAlign: "center",
                    },
                    "& .MuiDataGrid-cell": {
                      paddingLeft: 0,
                      paddingRight: 0,
                    },
                    "& .MuiDataGrid-columnHeader": {
                      paddingLeft: 0,
                      paddingRight: 0,
                    },
                  }}
                />
              </Box>
            </div>
            {totalCount > 0 && (
              <PaginationDefault
                limit={limit}
                offset={offset}
                totalCount={totalCount}
                onHandlePageChange={(newPage) => {
                  setOffset(newPage * limit);
                }}
                resetPageOnOffsetZero={true}
              />
            )}
            <Modals
              caseId={caseInfo?.id}
              modals={modals}
              setModals={setModals}
              modalInfo={modalInfo}
              parseData={parseData}
              handleSuccessModal={handleSuccessModal}
              handleFailureModal={handleFailureModal}
              handleRequestSuccess={handleRequestSuccess}
              errorArray={errorArray}
              selectedReferrals={selectedReferrals}
            />
          </>
        )}
      </div>
    </PermissionWrapper>
  );
}
